<template>
  <div class="engangsydelser text-dark">
    <h2 class="marg-1-bottom capitalize">Engangsydelser</h2>
    <template v-if="isNoConfiguration">
      <div class="engangsydelser-no-config">
        <h2>
          <i>Skolens Opus-integration er ikke konfigureret korrekt. Kontakt Docendo for at få hjælp til opsætningen -
            enten på chat eller email.</i>
        </h2>
      </div>
    </template>
    <template v-else>
      <div class="engangsydelser__header">
        <div class="engangsydelser__header__left">
          <Tabs
            :tabs="viewTabs"
            :selected="viewTab"
            @select="onSelectViewTab">
          </Tabs>
          <template v-if="viewTab === 'opus'">
            <DoFiltersFilter
              :title="'Personer'"
              :tags="personNames"
              :selectedTags="selectedPersonNames"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg personer'"
              @select="togglePersonName"
              @clear="selectedPersonNames = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Grupper'"
              :tags="userTags"
              :selectedTags="selectedUserTags"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg grupper'"
              @select="toggleUserTag"
              @clear="selectedUserTags = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="employmentTitles.length > 1"
              :title="'Ansættelser'"
              :tags="employmentTitles"
              :selectedTags="selectedEmploymentTitles"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg stilling'"
              @select="toggleEmploymentTitle"
              @clear="selectedEmploymentTitles = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="organisations.length > 1"
              :title="'Organisationer'"
              :tags="organisations"
              :selectedTags="selectedOrganisations"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg organisation'"
              @select="toggleOrganisation"
              @clear="selectedOrganisations = []">
            </DoFiltersFilter>
            <span class="engangsydelser__count">
              Viser {{ filteredRows.length }} af {{ allRows.length }} ansættelser
            </span>
          </template>
          <template v-else>
            <DoDatePickerRange
              :allowSameDate="true"
              :startDate="startDate"
              :endDate="endDate"
              :maximumDateRange="365"
              @startChanged="startDateChanged"
              @endChanged="endDateChanged">
            </DoDatePickerRange>
            <DoFiltersFilter
              :title="'Personer'"
              :tags="uniqueNames"
              :selectedTags="selectedNames"
              :numberOfSelectedTagsToShow="2"
              :placeholder="'Vælg personer'"
              @select="toggleName"
              @clear="clearNames">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Lønkode'"
              :tags="uniqueLoenart"
              :selectedTags="selectedLoenart"
              :numberOfSelectedTagsToShow="2"
              :placeholder="'Vælg lønkode'"
              @select="toggleLoenart"
              @clear="clearLoenart">
            </DoFiltersFilter>
          </template>
        </div>
        <div class="engangsydelser__header__right" v-if="viewTab !== 'opus'">
          <button class="do-bttn do-bttn--small" :disabled="isLoading" @click="readEngangsydelser">
            Opdater
          </button>
          <button class="do-bttn do-bttn--small"
                  :class="{'do-bttn--loading': isDownloadingExcel}"
                  :disabled="isLoading || isDownloadingExcel || displayedEngangsydelser.length === 0"
                  @click="downloadExcel">
            Download til excel
          </button>
        </div>
      </div>

      <div class="engangsydelser__toolbar" v-if="viewTab === 'opus'">
        <span class="engangsydelser__toolbar__wrap"
              :content="selectedEmploymentIds.length === 0 ? 'Vælg personer først' : ''"
              v-tippy="{
                arrow: true,
                placement: 'top',
                animateFill: false,
                animation: 'shift-away',
                boundary: 'window'
              }">
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--disabled': selectedEmploymentIds.length === 0}"
                  :disabled="selectedEmploymentIds.length === 0"
                  @click="openBulkCreate">
            Opret engangsydelser
          </button>
        </span>
        <button class="do-bttn" v-if="selectedEmploymentIds.length > 0" @click="cancelBatchMode">Annuller valg</button>
        <span class="engangsydelser__count" v-if="selectedEmploymentIds.length > 0">
          {{ selectedEmploymentIds.length }} valgt
        </span>
      </div>

      <div class="engangsydelser__body">
        <!-- Tab: Alle i OPUS -->
        <template v-if="viewTab === 'opus'">
          <Loader v-if="isLoadingEmployments"></Loader>
          <table v-else class="do-table">
            <thead>
            <tr>
              <th class="td-check do-table__toggle-all" @click="toggleAllSelected">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isAllFilteredSelected}"></div>
              </th>
              <th>NAVN</th>
              <th>ANSÆTTELSE</th>
              <th>ORGANISATION</th>
              <th class="th-row-actions"></th>
            </tr>
            </thead>
            <tbody v-if="filteredRows.length === 0">
            <tr>
              <td :colspan="5" class="italic">Ingen ansættelser fundet</td>
            </tr>
            </tbody>
            <tbody v-for="group in groupedRows" :key="group.userId" class="user-group">
            <tr v-for="row in group.rows"
                :key="row.id"
                class="employment-row"
                :class="{'is-selected': isRowSelected(row)}"
                @click="onRowClick(row)">
              <td class="td-check" @click.stop="onCheckboxClick(row)">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isRowSelected(row)}"></div>
              </td>
              <td class="bold">{{ row.userName }}</td>
              <td class="td-organisation">
                {{ row.employmentNumber }} - {{ row.employmentTitle }}
              </td>
              <td class="td-organisation">{{ row.organisation }}</td>
              <td class="td-row-actions">
                <button
                  v-if="!isBatchMode"
                  type="button"
                  class="do-bttn do-bttn--small row-action-btn"
                  @click.stop="openViewModal(row)"
                >Se engangsydelser</button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>

        <!-- Tabs: Sendt til lønsystem / Fejlede -->
        <template v-else>
          <Loader v-if="isLoading"></Loader>
          <table v-else class="do-table do-table--striped">
            <thead>
            <tr>
              <th
                class="th-sorter td-created"
                @click="setSort('vCreatedAt')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': !isSortPristine && sortBy==='vCreatedAt'}">
                OPRETTET <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter"
                @click="setSort('name')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='name'}">
                NAVN <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter"
                @click="setSort('employment_number')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='employment_number'}">
                ANSÆTTELSE <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter"
                @click="setSort('loenart')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='loenart'}">
                LØNKODE <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-units"
                @click="setSort('antal')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='antal'}">
                ENHEDER <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-units"
                @click="setSort('beloeb')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='beloeb'}">
                BELØB <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-date"
                @click="setSort('dato')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='dato'}">
                DATO <span class="icon-menu-open"></span>
              </th>

              <th>
                OPRETTET AF
              </th>

              <th>
                STATUS
              </th>

              <th class="th-actions">
              </th>

            </tr>
            </thead>
            <tbody>
            <tr v-if="displayedEngangsydelser.length === 0">
              <td :colspan="10" class="italic">
                Ingen engangsydelser fundet
              </td>
            </tr>
            <tr v-for="(item, index) in displayedEngangsydelser"
                :key="item.parentId + '-' + item.engangsydelseIndex + '-' + index"
                class="engangsydelse-row">
              <td class="td-created">{{ item.vCreatedAt }}</td>
              <td>{{ item.vUserName }}</td>
              <td>{{ item.vEmploymentNumber }}</td>
              <td>{{ item.vLoenart }}</td>
              <td class="td-units">{{ formatDecimal(item.antal) }}</td>
              <td class="td-units">{{ formatDecimal(item.beloeb) }}</td>
              <td class="td-date">{{ item.vDato }}</td>
              <td>{{ item.vCreatedBy }}</td>
              <td class="td-status">
                <span class="engangsydelse-status" :class="'engangsydelse-status--' + item.status" :title="item.vMessageText">
                  {{ statusLabel(item.status) }}
                </span>
                <div v-if="item.status === 'error' && item.vMessageText" class="engangsydelse-status__message" :title="item.vMessageText">
                  {{ item.vMessageText }}
                </div>
              </td>
              <td class="td-actions">
                <!-- Engangsydelser sendes automatisk til OPUS og kan ikke slettes;
                     afsendte korrigeres med en modpostering (negativt antal/beløb). -->
                <button
                  v-if="item.status === 'success'"
                  type="button"
                  class="do-bttn do-bttn--small modregn-btn"
                  @click.stop="openModpostering(item)"
                >Modregn</button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>
    <CreateEngangsydelse
      v-if="createEmploymentIds.length > 0"
      :schoolId="schoolId"
      :employmentIds="createEmploymentIds"
      :subtitle="createSubtitle"
      :prefill="createPrefill"
      @created="onCreated"
      @close="closeCreate">
    </CreateEngangsydelse>

    <EngangsydelserModal
      v-if="activeRow"
      :schoolId="schoolId"
      :employmentId="activeRow.employmentId"
      :employment="activeRow.employment"
      :userId="activeRow.userId"
      :userName="activeRow.userName"
      :employmentNumber="activeRow.employmentNumber"
      @created="onViewModalCreated"
      @close="activeRow = null">
    </EngangsydelserModal>
  </div>
</template>

<script>
import docendo from '../../../docendo'
import Loader from '../../common/Loader.vue'
import moment from 'moment'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import CreateEngangsydelse from './CreateEngangsydelse.vue'
import EngangsydelserModal from './EngangsydelserModal.vue'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'
import Tabs from '../../common/Tabs.vue'

let XLSX = null
export default {
  name: 'Engangsydelser',
  components: {Loader, DoFiltersFilter, CreateEngangsydelse, EngangsydelserModal, DoDatePickerRange, Tabs},
  data () {
    return {
      isNoConfiguration: false,
      isLoading: false,
      engangsydelser: [],
      sortBy: 'vCreatedAt',
      sortDir: 'desc',
      isSortPristine: true,
      selectedNames: [],
      selectedLoenart: [],
      // Engangsydelser kan være bag- og fremdaterede, så standardintervallet
      // dækker forrige måned til og med næste måned.
      startDate: moment().subtract(1, 'month').startOf('month').startOf('day'),
      endDate: moment().add(1, 'month').endOf('month').endOf('day'),
      viewTab: 'opus',
      // "Alle i OPUS"-fanen
      selectedPersonNames: [],
      selectedUserTags: [],
      selectedEmploymentTitles: [],
      selectedOrganisations: [],
      selectedEmploymentIds: [],
      isBatchMode: false,
      activeRow: null,
      createEmploymentIds: [],
      createSubtitle: '',
      createPrefill: null,
      isDownloadingExcel: false
    }
  },
  computed: {
    viewTabs () {
      return [
        {id: 'opus', title: 'Oprettelse'},
        {id: 'sent', title: `Sendt til lønsystem (${this.sentToSalarySystemCount})`},
        {id: 'failed', title: `Fejlede (${this.failedCount})`}
      ]
    },
    // Rækker inden for det valgte datointerval (aktivitetsdato). Rækker uden dato
    // beholdes, så ingen poster gemmes væk. Bruges af både tabellen og fane-tællerne.
    dateFilteredRows () {
      if (!this.startDate || !this.endDate) return this.engangsydelserSorted
      const from = moment(this.startDate).startOf('day')
      const to = moment(this.endDate).endOf('day')
      return this.engangsydelserSorted.filter(x => {
        if (!x.vDato) return true
        const dato = moment(x.vDato, 'DD-MM-YYYY')
        return dato.isSameOrAfter(from) && dato.isSameOrBefore(to)
      })
    },
    sentToSalarySystemCount () {
      return this.dateFilteredRows.filter(x => x.status !== 'error').length
    },
    failedCount () {
      return this.dateFilteredRows.filter(x => x.status === 'error').length
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    storageKey () {
      return 'engangsydelserFilters:' + (this.schoolId || 'default')
    },
    filtersSnapshot () {
      return JSON.stringify({
        viewTab: this.viewTab,
        selectedNames: this.selectedNames,
        selectedLoenart: this.selectedLoenart,
        selectedPersonNames: this.selectedPersonNames,
        selectedUserTags: this.selectedUserTags,
        selectedEmploymentTitles: this.selectedEmploymentTitles,
        selectedOrganisations: this.selectedOrganisations
      })
    },
    isLoadingEmployments () {
      return this.$store.state.employments && this.$store.state.employments.isLoadingEmploymentsTypesForSchool
    },
    users () {
      return this.$store.getters.usersSortedOnName || []
    },
    usersById () {
      const map = {}
      const list = this.users
      for (let i = 0; i < list.length; i++) {
        const u = list[i]
        map[u.id] = u.name
      }
      return map
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    // Ét row pr. (bruger, OPUS-ansættelse) med tjenestenummer.
    allRows () {
      const out = []
      for (let i = 0; i < this.users.length; i++) {
        const user = this.users[i]
        const emps = this.employmentsInUserIdHash[user.id] || []
        for (let j = 0; j < emps.length; j++) {
          const emp = emps[j]
          const empNumber = emp && emp.employment_number ? String(emp.employment_number) : ''
          if (empNumber.trim() === '') continue
          if (emp.source && emp.source !== 'opus') continue
          // Kun ansættelser med en stillingskategori kan vælges til engangsydelser.
          if (emp.employment_template_id == null) continue
          const title = emp.source_title || emp.title || 'Ansættelse'
          out.push({
            id: user.id + '_' + emp.id,
            userId: user.id,
            userName: user.name,
            userTags: Array.isArray(user.user_tags) ? user.user_tags : [],
            employmentId: emp.id,
            employmentTitle: title,
            employmentNumber: empNumber,
            organisation: emp.organisation || '',
            employment: emp
          })
        }
      }
      return out.sort((a, b) => a.userName.localeCompare(b.userName, 'da-DK', {sensitivity: 'base'}))
    },
    personNames () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].userName) set[this.allRows[i].userName] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    userTags () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        const t = this.allRows[i].userTags || []
        for (let j = 0; j < t.length; j++) {
          if (t[j]) set[t[j]] = true
        }
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    employmentTitles () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].employmentTitle) set[this.allRows[i].employmentTitle] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    organisations () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].organisation) set[this.allRows[i].organisation] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    filteredRows () {
      return this.allRows.filter(row => {
        if (this.selectedPersonNames.length > 0 && this.selectedPersonNames.indexOf(row.userName) === -1) {
          return false
        }
        if (this.selectedUserTags.length > 0) {
          const match = row.userTags && this.selectedUserTags.some(t => row.userTags.indexOf(t) > -1)
          if (!match) return false
        }
        if (this.selectedEmploymentTitles.length > 0 && this.selectedEmploymentTitles.indexOf(row.employmentTitle) === -1) {
          return false
        }
        if (this.selectedOrganisations.length > 0 && this.selectedOrganisations.indexOf(row.organisation) === -1) {
          return false
        }
        return true
      })
    },
    isAllFilteredSelected () {
      if (this.filteredRows.length === 0) return false
      return this.filteredRows.every(row => this.selectedEmploymentIds.indexOf(row.employmentId) > -1)
    },
    // Grupperer rækkerne pr. person, så baggrundsfarven banderer pr. person og ikke pr. række.
    groupedRows () {
      const groups = []
      const indexByUser = {}
      for (let i = 0; i < this.filteredRows.length; i++) {
        const row = this.filteredRows[i]
        if (indexByUser[row.userId] === undefined) {
          indexByUser[row.userId] = groups.length
          groups.push({userId: row.userId, rows: []})
        }
        groups[indexByUser[row.userId]].rows.push(row)
      }
      return groups
    },
    employmentsAll () {
      return this.$store.state.employments && this.$store.state.employments.employments
        ? this.$store.state.employments.employments
        : []
    },
    employmentsById () {
      const map = {}
      const list = this.employmentsAll || []
      for (let i = 0; i < list.length; i++) {
        const e = list[i]
        map[e.id] = e
      }
      return map
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    salaryCodesByLoenart () {
      const map = {}
      const codes = this.salaryCodes || []
      for (let i = 0; i < codes.length; i++) {
        const code = codes[i]
        if (code.loenart) {
          map[String(code.loenart)] = code
        }
      }
      return map
    },
    salaryCodesById () {
      const map = {}
      const codes = this.salaryCodes || []
      for (let i = 0; i < codes.length; i++) {
        map[codes[i].id] = codes[i]
      }
      return map
    },
    engangsydelserSorted () {
      const result = []
      for (let i = 0; i < this.engangsydelser.length; i++) {
        const item = this.engangsydelser[i]
        const employeeId = item.employee_id || item.user_id
        const employeeUser = this.usersById[employeeId] || null
        const vUserName = employeeUser || (item.medarbejdernummer ? String(item.medarbejdernummer) : 'Ukendt')

        // Find employment baseret på employment_id
        const employment = item.employment_id ? this.employmentsById[item.employment_id] : null
        const vEmploymentNumber = employment && employment.employment_number
          ? employment.employment_number + (employment.title ? ' - ' + employment.title : '')
          : (item.medarbejdernummer ? String(item.medarbejdernummer) : 'Ukendt')

        // Find navn på den der oprettede (user_id)
        const createdByUser = item.user_id ? this.usersById[item.user_id] : null
        const vCreatedBy = createdByUser || ''

        const vCreatedAt = item.created_at ? moment(item.created_at).format('lll') : ''
        const vStatus = item.status || 'new'
        const vMessage = item.message || null
        const vMessageText = this.formatMessage(vMessage)

        // Hvis der er engangsydelser array, lav en række for hver
        if (item.engangsydelser && Array.isArray(item.engangsydelser) && item.engangsydelser.length > 0) {
          for (let j = 0; j < item.engangsydelser.length; j++) {
            const engangsydelse = item.engangsydelser[j]
            const vDato = engangsydelse.Startdato
              ? moment(engangsydelse.Startdato).format('DD-MM-YYYY')
              : ''
            // Payloadens Loenart er den 4-cifrede, nulpaddede KMD-lønart (fx '0468'), mens
            // Docendo-lønkoden er lønart uden foranstillet nul + løbenummer (fx '4683').
            const loenartCode = engangsydelse.Loenart || ''
            const loebenummerCode = engangsydelse.Loebenummer != null ? String(engangsydelse.Loebenummer) : ''
            const loenkode = loenartCode
              ? String(parseInt(loenartCode, 10)).padStart(3, '0') + loebenummerCode
              : ''
            const salaryCode = (item.salary_code_id && this.salaryCodesById[item.salary_code_id]) ||
              (loenkode ? this.salaryCodesByLoenart[loenkode] : null)
            const vLoenart = salaryCode && salaryCode.name
              ? (salaryCode.loenart || loenkode) + ' - ' + salaryCode.name
              : loenkode
            result.push({
              id: item.id + '-' + j,
              parentId: item.id,
              engangsydelseIndex: j,
              vUserName,
              vEmploymentNumber,
              vCreatedAt,
              vCreatedBy,
              vDato,
              loenart: loenkode,
              vLoenart: vLoenart,
              salaryCodeId: salaryCode ? salaryCode.id : null,
              loebenummer: engangsydelse.Loebenummer || '',
              antal: engangsydelse.Antal != null ? engangsydelse.Antal : null,
              beloeb: engangsydelse.Beloeb != null ? engangsydelse.Beloeb : null,
              status: vStatus,
              message: vMessage,
              vMessageText,
              user_id: item.user_id,
              employee_id: item.employee_id,
              employment_id: item.employment_id,
              medarbejdernummer: item.medarbejdernummer,
              created_at: item.created_at,
              originalItem: item,
              originalEngangsydelse: engangsydelse
            })
          }
        } else {
          // Hvis der ikke er engangsydelser array, lav en tom række
          result.push({
            id: item.id + '-empty',
            parentId: item.id,
            engangsydelseIndex: -1,
            vUserName,
            vEmploymentNumber,
            vCreatedAt,
            vCreatedBy,
            vDato: '',
            loenart: '',
            vLoenart: '',
            salaryCodeId: null,
            loebenummer: '',
            antal: null,
            beloeb: null,
            status: vStatus,
            message: vMessage,
            vMessageText,
            user_id: item.user_id,
            employee_id: item.employee_id,
            employment_id: item.employment_id,
            medarbejdernummer: item.medarbejdernummer,
            created_at: item.created_at,
            originalItem: item,
            originalEngangsydelse: null
          })
        }
      }
      return result
    },
    uniqueNames () {
      const set = {}
      for (let i = 0; i < this.engangsydelserSorted.length; i++) {
        const s = this.engangsydelserSorted[i]
        const name = s.vUserName || ''
        if (name) set[name] = true
      }
      const arr = Object.keys(set)
      return arr.sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    uniqueLoenart () {
      const set = {}
      for (let i = 0; i < this.engangsydelserSorted.length; i++) {
        const s = this.engangsydelserSorted[i]
        const loenart = s.loenart || ''
        if (loenart) set[loenart] = true
      }
      const arr = Object.keys(set)
      return arr.sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    displayedEngangsydelser () {
      let rows = this.dateFilteredRows.slice()

      // Filtrer på tab (sendt/fejlede)
      if (this.viewTab === 'failed') {
        rows = rows.filter(x => x.status === 'error')
      } else {
        rows = rows.filter(x => x.status !== 'error')
      }

      // Filtrer på navne
      if (this.selectedNames && this.selectedNames.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedNames.length; i++) chosen[this.selectedNames[i]] = true
        rows = rows.filter(x => chosen[x.vUserName || ''])
      }

      // Filtrer på lønart
      if (this.selectedLoenart && this.selectedLoenart.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedLoenart.length; i++) chosen[this.selectedLoenart[i]] = true
        rows = rows.filter(x => chosen[x.loenart || ''])
      }

      const get = (item, key) => {
        switch (key) {
          case 'name':
            return item.vUserName || ''
          case 'employment_number':
            return String(item.vEmploymentNumber || '')
          case 'loenart':
            return String(item.loenart || '')
          case 'loebenummer':
            return String(item.loebenummer || '')
          case 'dato':
            return item.vDato ? moment(item.vDato, 'DD-MM-YYYY').toDate() : new Date(0)
          case 'antal':
            return this._num(item.antal)
          case 'beloeb':
            return this._num(item.beloeb)
          case 'vCreatedAt':
            return item.created_at ? this._momentOrNull(item.created_at).valueOf() : 0
          default:
            return item[key] || ''
        }
      }

      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortBy

      return rows.slice().sort((a, b) => {
        const va = get(a, key)
        const vb = get(b, key)
        if (va === vb) return 0
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        if (va instanceof Date && vb instanceof Date) return (va - vb) * dir
        if (typeof va === 'number' && typeof vb !== 'number') return -1 * dir
        if (typeof vb === 'number' && typeof va !== 'number') return 1 * dir
        return String(va).localeCompare(String(vb), 'da', {sensitivity: 'base'}) * dir
      })
    }
  },
  methods: {
    onSelectViewTab (tab) {
      this.viewTab = tab
      // Genindlæs listen når Sendt/Fejlede vælges, så status er aktuel.
      if (tab !== 'opus' && !this.isLoading) {
        this.readEngangsydelser()
      }
    },
    togglePersonName (name) {
      const idx = this.selectedPersonNames.indexOf(name)
      if (idx > -1) this.selectedPersonNames.splice(idx, 1)
      else this.selectedPersonNames.push(name)
    },
    toggleUserTag (tag) {
      const idx = this.selectedUserTags.indexOf(tag)
      if (idx > -1) this.selectedUserTags.splice(idx, 1)
      else this.selectedUserTags.push(tag)
    },
    toggleEmploymentTitle (title) {
      const idx = this.selectedEmploymentTitles.indexOf(title)
      if (idx > -1) this.selectedEmploymentTitles.splice(idx, 1)
      else this.selectedEmploymentTitles.push(title)
    },
    toggleOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) this.selectedOrganisations.splice(idx, 1)
      else this.selectedOrganisations.push(organisation)
    },
    isRowSelected (row) {
      return this.selectedEmploymentIds.indexOf(row.employmentId) > -1
    },
    toggleSelection (row) {
      const idx = this.selectedEmploymentIds.indexOf(row.employmentId)
      if (idx > -1) this.selectedEmploymentIds.splice(idx, 1)
      else this.selectedEmploymentIds.push(row.employmentId)
    },
    toggleAllSelected () {
      this.isBatchMode = true
      const visibleIds = this.filteredRows.map(row => row.employmentId)
      if (this.isAllFilteredSelected) {
        // Fravælg alle synlige
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => visibleIds.indexOf(id) === -1)
      } else {
        // Tilføj alle synlige
        const set = {}
        for (let i = 0; i < this.selectedEmploymentIds.length; i++) set[this.selectedEmploymentIds[i]] = true
        for (let i = 0; i < visibleIds.length; i++) set[visibleIds[i]] = true
        this.selectedEmploymentIds = Object.keys(set).map(id => parseInt(id, 10))
      }
    },
    cancelBatchMode () {
      this.isBatchMode = false
      this.selectedEmploymentIds = []
    },
    onRowClick (row) {
      // Uden for bulk-mode åbner et rækkeklik visningen af indberettede engangsydelser; i bulk-mode toggles valget.
      if (this.isBatchMode) {
        this.toggleSelection(row)
      } else {
        this.openViewModal(row)
      }
    },
    openViewModal (row) {
      this.activeRow = row
    },
    onViewModalCreated () {
      // En ny engangsydelse er sendt til behandling; genindlæs hovedlisten så Sendt/Fejlede er opdateret.
      this.readEngangsydelser()
    },
    onCheckboxClick (row) {
      // Klik direkte på checkboxen slår bulk-mode til og vælger ansættelsen.
      if (!this.isBatchMode) this.isBatchMode = true
      this.toggleSelection(row)
    },
    openBulkCreate () {
      if (this.selectedEmploymentIds.length === 0) return
      this.createEmploymentIds = this.selectedEmploymentIds.slice()
      this.createSubtitle = ''
    },
    closeCreate () {
      this.createEmploymentIds = []
      this.createSubtitle = ''
      this.createPrefill = null
    },
    // Afsendte engangsydelser er immutable i OPUS; korrektion sker via en negativ postering.
    // Åbner opret-modalen forudfyldt med fortegnsvendt antal/beløb på samme ansættelse og dato.
    openModpostering (item) {
      if (!item.employment_id) return
      this.createSubtitle = 'Modregn – ' + item.vUserName + ' – ' + item.vEmploymentNumber
      this.createPrefill = {
        salaryCodeId: item.salaryCodeId || null,
        antal: item.antal != null ? -item.antal : null,
        beloeb: item.beloeb != null ? -item.beloeb : null,
        dato: item.vDato ? moment(item.vDato, 'DD-MM-YYYY') : null
      }
      this.createEmploymentIds = [item.employment_id]
    },
    onCreated () {
      this.closeCreate()
      this.cancelBatchMode()
      this.readEngangsydelser()
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx >= 0) {
        this.selectedNames.splice(idx, 1)
      } else {
        this.selectedNames.push(name)
      }
    },
    clearNames () {
      this.selectedNames = []
    },
    toggleLoenart (loenart) {
      const idx = this.selectedLoenart.indexOf(loenart)
      if (idx >= 0) {
        this.selectedLoenart.splice(idx, 1)
      } else {
        this.selectedLoenart.push(loenart)
      }
    },
    clearLoenart () {
      this.selectedLoenart = []
    },
    setSort (key) {
      this.isSortPristine = false
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = 'asc'
      }
    },
    _num (v) {
      if (v == null || v === '') return 0
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
      return Number.isFinite(n) ? n : 0
    },
    _momentOrNull (iso) {
      return iso ? moment(iso) : null
    },
    formatDecimal (value) {
      if (value == null || value === '') return ''
      return String(value).replace('.', ',')
    },
    readEngangsydelser () {
      this.isLoading = true
      docendo.listKmdEngangsydelser(this.schoolId, success => {
        if (success && success.status && success.status === 'error') {
          this.isNoConfiguration = true
        }
        if (Array.isArray(success)) {
          this.engangsydelser = success
        } else if (success && Array.isArray(success.data)) {
          this.engangsydelser = success.data
        } else {
          this.engangsydelser = []
        }
        this.isLoading = false
      }, failure => {
        console.log(failure)
        this.isLoading = false
        this.engangsydelser = []
        if (failure && failure.status && failure.status === 'error') {
          this.isNoConfiguration = true
        }
      })
    },
    startDateChanged (date) {
      this.startDate = date
      this.readEngangsydelser()
    },
    endDateChanged (date) {
      this.endDate = date
      this.readEngangsydelser()
    },
    statusLabel (status) {
      switch (status) {
        case 'new': return 'Afventer'
        case 'success': return 'Sendt'
        case 'error': return 'Fejlet'
        default: return status || ''
      }
    },
    // Eksporterer de aktuelt viste rækker (samme filtre og sortering som tabellen).
    dataToExcel () {
      return this.displayedEngangsydelser.map(row => ({
        created_at: row.vCreatedAt || '',
        user: row.vUserName || '',
        employment: row.vEmploymentNumber || '',
        loenkode: row.vLoenart || '',
        antal: row.antal != null ? row.antal : '',
        beloeb: row.beloeb != null ? row.beloeb : '',
        dato: row.vDato || '',
        created_by: row.vCreatedBy || '',
        status: this.statusLabel(row.status),
        message: row.vMessageText || ''
      }))
    },
    downloadExcel () {
      if (this.isDownloadingExcel || this.displayedEngangsydelser.length === 0) return
      this.isDownloadingExcel = true
      if (window.XLSX && XLSX) {
        this.writeExcel()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        const intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.writeExcel()
          }
        }, 500)
      }
    },
    writeExcel () {
      const headers = {
        created_at: 'Oprettet',
        user: 'Navn',
        employment: 'Ansættelse',
        loenkode: 'Lønkode',
        antal: 'Enheder',
        beloeb: 'Beløb',
        dato: 'Dato',
        created_by: 'Oprettet af',
        status: 'Status',
        message: 'Besked'
      }
      const rows = this.dataToExcel()
      rows.unshift(headers)
      const sheet = XLSX.utils.json_to_sheet(rows, {skipHeader: true})
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, sheet, 'data')
      const label = this.viewTab === 'failed' ? 'fejlede' : 'sendt'
      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      XLSX.writeFile(wb, `engangsydelser_${label}_${timestamp}.xlsx`)
      this.isDownloadingExcel = false
    },
    // KMD's Meddelelse er typisk et array af {Kode, Type, Tekst}, men kan også være et enkelt objekt.
    // Vi viser kode + tekst, som dokumentationen anbefaler ifm. fejlsøgning med lønkontoret.
    formatMessage (message) {
      if (!message) return ''
      const list = Array.isArray(message) ? message : [message]
      return list
        .map(m => {
          if (!m) return ''
          if (typeof m === 'string') return m
          const tekst = m.Tekst || m.tekst || m.message || ''
          return m.Kode != null && tekst ? m.Kode + ': ' + tekst : tekst
        })
        .filter(t => t)
        .join(' · ')
    },
    persistFilters (val) {
      try {
        window.localStorage.setItem(this.storageKey, val)
      } catch (e) {
        // ignore storage write errors (e.g. private mode / quota)
      }
    },
    restoreFilters () {
      try {
        const raw = window.localStorage.getItem(this.storageKey)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved) return
        if (saved.viewTab) this.viewTab = saved.viewTab
        if (Array.isArray(saved.selectedNames)) this.selectedNames = saved.selectedNames
        if (Array.isArray(saved.selectedLoenart)) this.selectedLoenart = saved.selectedLoenart
        if (Array.isArray(saved.selectedPersonNames)) this.selectedPersonNames = saved.selectedPersonNames
        if (Array.isArray(saved.selectedUserTags)) this.selectedUserTags = saved.selectedUserTags
        if (Array.isArray(saved.selectedEmploymentTitles)) this.selectedEmploymentTitles = saved.selectedEmploymentTitles
        if (Array.isArray(saved.selectedOrganisations)) this.selectedOrganisations = saved.selectedOrganisations
      } catch (e) {
        // ignore malformed storage
      }
    }
  },
  watch: {
    filtersSnapshot (val) {
      this.persistFilters(val)
    },
    selectedEmploymentIds (ids) {
      // Forlad bulk-mode automatisk når intet er valgt
      if (!ids || ids.length === 0) {
        this.isBatchMode = false
      }
    }
  },
  created () {
    this.restoreFilters()
  },
  mounted () {
    this.$store.dispatch('readEmployments')
    this.$store.dispatch('readSalaryCodes')
    this.readEngangsydelser()
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.engangsydelser {
  padding: 1rem;
}

.engangsydelser__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.engangsydelser__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.engangsydelser__header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-left: auto;
}

.engangsydelser__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.engangsydelser__toolbar__wrap {
  display: inline-flex;
}

.engangsydelser__count {
  font-style: italic;
  font-size: 11px;
  color: #6b7280;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table tbody td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.do-table tbody tr {
  transition: background-color 0.15s;
}

.do-table tbody tr:hover {
  background-color: aliceblue;
}

/* Banding pr. person (tbody-gruppe) i "Alle i OPUS"-tabellen */
.do-table tbody.user-group:nth-of-type(2n) {
  background-color: #f9fafb;
}

.do-table tbody.user-group tr {
  cursor: pointer;
}

/* Zebra-striber pr. række i sendt/fejlede-tabellen */
.do-table--striped tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.td-check {
  width: 26px;
}

.do-table thead th.td-check {
  border-right: 1px solid #e2e8f0;
}

.do-table tbody td.td-check {
  vertical-align: middle;
}

.do-table__toggle-all {
  color: var(--primary, #468ad7);
  cursor: pointer;
  user-select: none;
  text-transform: none;
  white-space: nowrap;
}

.do-table__toggle-all:hover {
  text-decoration: underline;
}

.do-table tbody tr.is-selected {
  background-color: #eaf3fb;
}

.do-checkbox {
  border: 1px solid #ccc;
  border-radius: .25rem;
  height: 1rem;
  position: relative;
  width: 1rem;
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
}

.do-checkbox.do-checkbox--selected {
  background: var(--primary);
  border-color: var(--primary);
}

.do-checkbox.do-checkbox--selected:after,
.do-checkbox.do-checkbox--selected:before {
  background: #fff;
}

.do-checkbox:after,
.do-checkbox:before {
  background: transparent;
  transform: rotate(35deg);
}

.do-checkbox:after {
  height: 8px;
  right: 3px;
  top: 1px;
  width: 1.5px;
}

.do-checkbox:after,
.do-checkbox:before {
  border-radius: 1rem;
  content: "";
  display: block;
  position: absolute;
}

.do-checkbox.do-checkbox--selected:before {
  bottom: 2.5px;
  height: 1.5px;
  right: 5px;
  width: 4px;
}

.td-organisation {
  opacity: 0.7;
  font-size: 11px;
}

.text-link {
  color: var(--primary, #468AD7);
  font-weight: 500;
}

.employment-row:hover .text-link {
  text-decoration: underline;
}

.th-row-actions {
  width: 170px;
}

.td-row-actions {
  width: 170px;
  text-align: right;
  position: relative;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Knappen placeres absolut, så rækken ikke bliver højere end i bulk-mode */
.row-action-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.12s ease;
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.employment-row:hover .row-action-btn {
  opacity: 1;
  pointer-events: auto;
}

.th-sorter {
  cursor: pointer;
}

.th-sorter:hover {
  transition: 0.15s;
}

.th-sorter:active {
  transform: scale(0.98);
}

.th-sorter.th-sorter--active {
  color: var(--primary);
}

.td-date, .td-units {
  width: 100px;
}

.td-units {
  text-align: right;
  width: 80px;
}

.do-table thead th.td-units {
  text-align: right;
}

.td-created {
  width: 150px;
}

.capitalize {
  text-transform: capitalize;
}

.engangsydelse-row {
  position: relative;
}

.modregn-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.12s ease;
}

.engangsydelse-row:hover .modregn-btn {
  opacity: 1;
  pointer-events: auto;
}

.td-actions {
  width: 150px;
  text-align: right;
  position: relative;
}

.th-actions {
  width: 150px;
}

.engangsydelse-status {
  font-weight: 600;
  white-space: nowrap;
}

.engangsydelse-status--new {
  color: #b45309;
}

.engangsydelse-status--success {
  color: #15803d;
}

.engangsydelse-status--error {
  color: var(--danger);
}

.engangsydelse-status__message {
  font-size: 12px;
  color: #6b7280;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// Engangsydelser.vue?12422407